/**
 * ForgotPasswordForm Component
 * 
 * Email input form for initiating password reset flow.
 * Sends password reset link to user's email.
 * 
 * Requirements: 3.1
 */

import React, { useState } from 'react';
import { validateEmail } from '../utils/validation.utils';
import { ErrorAlert } from './ErrorAlert';
import { LoadingSpinner } from './LoadingSpinner';

export interface ForgotPasswordFormProps {
  onSubmit: (email: string) => Promise<void>;
  onBackToLogin?: () => void;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ 
  onSubmit, 
  onBackToLogin 
}) => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!email.trim()) {
      newErrors.push('Email is required');
    } else if (!validateEmail(email)) {
      newErrors.push('Please enter a valid email address');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors([]);

    try {
      await onSubmit(email);
      setIsSuccess(true);
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'Failed to send reset email']);
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #3498db',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
    color: '#333333',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
  };

  const buttonStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px',
    backgroundColor: '#3498db',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: isLoading ? 'not-allowed' : 'pointer',
    opacity: isLoading ? 0.7 : 1,
  };

  if (isSuccess) {
    return (
      <div className="forgot-password-success">
        <div style={{ 
          backgroundColor: '#e8f5e9', 
          border: '1px solid #c8e6c9',
          borderRadius: '4px',
          padding: '16px',
          marginBottom: '24px',
          color: '#2e7d32',
        }}>
          <strong>Check your email</strong>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
            We've sent a password reset link to <strong>{email}</strong>. 
            Please check your inbox and follow the instructions.
          </p>
        </div>
        
        {onBackToLogin && (
          <button
            type="button"
            onClick={onBackToLogin}
            style={{
              ...buttonStyle,
              backgroundColor: '#fff',
              color: '#3498db',
              border: '1px solid #3498db',
            }}
          >
            Back to Login
          </button>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="forgot-password-form">
      {errors.length > 0 && <ErrorAlert message={errors} onDismiss={() => setErrors([])} />}

      <p style={{ marginBottom: '24px', fontSize: '14px', color: '#666' }}>
        Enter your email address and we'll send you a link to reset your password.
      </p>

      <div style={{ marginBottom: '24px' }}>
        <label htmlFor="email" style={labelStyle}>
          Email Address
        </label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
          autoComplete="email"
          style={inputStyle}
          aria-required="true"
        />
      </div>

      <button type="submit" disabled={isLoading} style={buttonStyle}>
        {isLoading ? <LoadingSpinner size="small" /> : 'Send Reset Link'}
      </button>

      {onBackToLogin && (
        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <button
            type="button"
            onClick={onBackToLogin}
            disabled={isLoading}
            style={{
              background: 'none',
              border: 'none',
              color: '#3498db',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: '14px',
            }}
          >
            Back to Login
          </button>
        </div>
      )}
    </form>
  );
};
